import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Dropdown } from 'ncoded-component-library';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import DotshorizontalIcon from 'icons/Dotshorizontal.icon';
import { DropdownOption } from 'models/Dropdown';
import { ALIGNMENT, Alignment } from 'models/Global';
import { getScrollParent } from 'utils';

import './ThreeDotsDropdown.styles.scss';

const MENU_GAP = 8;
const VIEWPORT_OFFSET = 8;

type ThreeDotsDropdownProps = {
  className?: string;
  options: DropdownOption[];
  alignment?: Alignment;
  withBorder?: boolean;
  /**
   * Portals the menu and pins it to the trigger in viewport coordinates, flipping it
   * upwards when it doesn't fit in the trigger's scroll container. For triggers inside
   * a fixed layer (a modal), where the library's page-based position drifts on iOS.
   */
  anchorToTrigger?: boolean;
} & Partial<Omit<DropdownProps, 'options' | 'ref'>>;

const ThreeDotsDropdown: React.ForwardRefRenderFunction<
  DropdownRef,
  ThreeDotsDropdownProps
> = (props, ref) => {
  const {
    className,
    options,
    trigger,
    withBorder = true,
    alignment = ALIGNMENT.HORIZONTAL,
    anchorToTrigger,
    contentClassName,
    onOpen,
    onClose,
    ...rest
  } = props;

  const classes = classNames('three-dots-dropdown', className);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  // the library's Button forwards only callback refs, an object ref stays null
  const [isOpen, setIsOpen] = useState(false);

  const [triggerEl, triggerRef] = useCallbackRef<HTMLButtonElement>();

  const menuRef = useRef<HTMLUListElement>(null);

  useImperativeHandle(ref, () => dropdown, [dropdown]);

  const stopEvent = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  // the library's inline top/left are overridden in CSS through these variables
  const placeMenu = useCallback(() => {
    const content = menuRef.current?.parentElement;

    if (!triggerEl || !content) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const { width, height } = content.getBoundingClientRect();
    const boundary = getScrollParent(triggerEl).getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    // below it has to fit in the scroll container; above, it may cover what sits over it
    const spaceBelow =
      Math.min(boundary.bottom, viewportHeight) - triggerRect.bottom - MENU_GAP;
    const spaceAbove = triggerRect.top - VIEWPORT_OFFSET - MENU_GAP;

    const isUpwards = spaceBelow < height && spaceAbove >= height;

    const top = isUpwards
      ? triggerRect.top - MENU_GAP - height
      : triggerRect.bottom + MENU_GAP;
    const left = Math.max(triggerRect.right - width, VIEWPORT_OFFSET);

    content.style.setProperty('--three-dots-menu-top', `${top}px`);
    content.style.setProperty('--three-dots-menu-left', `${left}px`);
    content.dataset.placed = 'true';
  }, [triggerEl]);

  useEffect(() => {
    if (!anchorToTrigger || !isOpen) return;

    placeMenu();

    const content = menuRef.current?.parentElement;
    const { visualViewport } = window;

    window.addEventListener('resize', placeMenu);
    visualViewport?.addEventListener('resize', placeMenu);

    return () => {
      window.removeEventListener('resize', placeMenu);
      visualViewport?.removeEventListener('resize', placeMenu);
      content?.removeAttribute('data-placed');
    };
  }, [anchorToTrigger, isOpen, placeMenu]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const dropdownTrigger = useMemo(
    () =>
      trigger || (
        <Button
          ref={triggerRef}
          className={classNames('three-dots-dropdown__trigger', alignment, {
            withBorder,
          })}
          variant="icon"
          icon={DotshorizontalIcon}
          onMouseDown={stopEvent}
          onMouseUp={stopEvent}
          aria-label="Open"
        />
      ),
    [alignment, trigger, withBorder, stopEvent, triggerRef],
  );

  return (
    <Dropdown
      ref={dropdownRef}
      className={classes}
      trigger={dropdownTrigger}
      {...(anchorToTrigger && { renderAsPortal: true })}
      contentClassName={classNames(contentClassName, {
        'three-dots-dropdown__content--anchored': anchorToTrigger,
      })}
      onOpen={handleOpen}
      onClose={handleClose}
      {...rest}
    >
      <ul
        ref={menuRef}
        className="three-dots-dropdown__menu"
        onContextMenu={stopEvent}
        onMouseDown={stopEvent}
        onMouseUp={stopEvent}
      >
        {options.map(({ label, icon, alert, disabled, onClick }, index) => (
          <li
            key={index}
            className={classNames({ delete: alert, disabled })}
            onClick={(e) => {
              if (disabled) return;

              onClick?.(e);
              dropdown.setIsOpen(false);
            }}
          >
            {icon}
            {label}
          </li>
        ))}
      </ul>
    </Dropdown>
  );
};

export default forwardRef(ThreeDotsDropdown);
